import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, formatQuantityMilli, PRODUCT_IMAGE_LIMITS, PRODUCT_KIND_LABELS, type Product } from '@kontor/shared';
import { api, ApiError, apiUpload } from '../../api';
import { toneClass } from '../../components/color/ColorSwatches';
import { useSession } from '../../session';
import { cmText, imageUrl, kgText, useProductLookups } from './lookups';
import './products.css';

const stamp = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
const sizeText = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`);

/** Ansicht eines Produkts mit Bildern (KON-108, KON-109). Bearbeiten, archivieren und löschen je nach Recht. */
export function ProductDetailPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const lookups = useProductLookups();
  const [message, setMessage] = useState<string | null>(null);
  const product = useQuery({ queryKey: ['product', id], queryFn: async () => (await api<{ data: Product }>('GET', `/products/${id}`)).data });
  const set = (p: Product) => {
    client.setQueryData(['product', p.id], p);
    client.invalidateQueries({ queryKey: ['products'] });
  };

  const toggle = useMutation({
    mutationFn: async (isActive: boolean) => (await api<{ data: Product }>('PATCH', `/products/${id}`, { isActive })).data,
    onSuccess: set,
    onError: (e) => setMessage(e instanceof ApiError ? e.message : 'Das hat nicht geklappt.'),
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/products/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['products'] });
      navigate('/produkte', { replace: true });
    },
    onError: (e) => setMessage(e instanceof ApiError ? e.message : 'Das Produkt konnte nicht gelöscht werden.'),
  });

  if (product.isLoading) return <p className="muted">Wird geladen …</p>;
  if (product.isError || !product.data) {
    return (
      <div className="card narrow">
        <h1>Produkt nicht gefunden</h1>
        <Link to="/produkte">Zur Liste</Link>
      </div>
    );
  }
  const p = product.data;
  const write = can('products.write');
  const margin = p.purchasePriceCents !== null && p.salePriceCents > 0 ? p.salePriceCents - p.purchasePriceCents : null;
  const dims = [p.lengthMm, p.widthMm, p.heightMm].some((v) => v !== null) ? `${[p.lengthMm, p.widthMm, p.heightMm].map((v) => (v === null ? '–' : cmText(v))).join(' × ')} cm` : null;
  const tags = p.tagIds.map((t) => lookups.tags.find((x) => x.id === t)).filter((t): t is NonNullable<typeof t> => !!t);
  const fact = (label: string, value: React.ReactNode) => (value === null || value === undefined || value === '' ? null : (<><dt>{label}</dt><dd>{value}</dd></>));

  return (
    <>
      <p><Link to="/produkte" className="back-link">← Alle Produkte</Link></p>
      <div className="page-head">
        <h1>{p.name}</h1>
        {!p.isActive && <span className="badge">Archiviert</span>}
        {!p.isSellable && <span className="badge">Nicht im Verkauf</span>}
        {write && <Link className="btn btn--secondary btn--sm page-head__action" to={`/produkte/${p.id}/bearbeiten`}>Bearbeiten</Link>}
      </div>
      <p className="muted">{[p.sku && `Artikelnummer ${p.sku}`, PRODUCT_KIND_LABELS[p.kind], lookups.categoryLabel(p.categoryId)].filter(Boolean).join(' · ')}</p>
      {message && <div className="alert alert--error" role="alert">{message}</div>}

      <div className="detail-grid">
        <ProductImages product={p} onChange={set} canWrite={write} />

        <section className="card">
          <h2>Preis</h2>
          <dl className="facts">
            <dt>Verkauf netto</dt>
            <dd><strong>{formatCents(p.salePriceCents)}</strong>{p.unitSymbol && <span className="muted"> je {p.unitSymbol}</span>}</dd>
            <dt>Steuersatz</dt>
            <dd>{p.taxRate} %</dd>
            {fact('Einkauf netto', p.purchasePriceCents !== null ? formatCents(p.purchasePriceCents) : null)}
            {fact('Marge', margin !== null ? `${formatCents(margin)} (${Math.round((margin / p.salePriceCents) * 100)} %)` : null)}
            {p.stockMilli !== null && (
              <>
                <dt>Lagerbestand</dt>
                <dd>
                  <strong>{formatQuantityMilli(p.stockMilli)}</strong>{p.unitSymbol && <span className="muted"> {p.unitSymbol}</span>} · {p.stockMilli > 0 ? 'verfügbar' : <span className="inv-bad">nicht verfügbar</span>}
                  {can('inventory.read') && <> · <Link to={`/lager/bestand/${p.id}`}>im Lager</Link></>}
                </dd>
              </>
            )}
          </dl>
          {tags.length > 0 && (
            <div className="tag-badges product-detail__tags">
              {tags.map((t) => <span key={t.id} className={`tag-badge ${toneClass(t.color)}`}>{t.name}</span>)}
            </div>
          )}
          {p.description && <p className="message">{p.description}</p>}
        </section>
      </div>

      <section className="card">
        <h2>Details</h2>
        <dl className="facts facts--wide">
          {fact('Lieferant', p.supplierId ? <Link to={`/kontakte/${p.supplierId}`} className="strong-link">{p.supplierName}</Link> : null)}
          {fact('Artikelnummer beim Lieferanten', p.supplierSku)}
          {fact('EAN / GTIN', p.ean)}
          {fact('Hersteller', p.manufacturer)}
          {fact('Herstellernummer', p.manufacturerSku)}
          {fact('Gewicht', p.weightGrams !== null ? `${kgText(p.weightGrams)} kg` : null)}
          {fact('Maße (L × B × H)', dims)}
          <dt>Angelegt</dt>
          <dd>{stamp.format(new Date(p.createdAt))}</dd>
          <dt>Zuletzt geändert</dt>
          <dd>{stamp.format(new Date(p.updatedAt))}</dd>
        </dl>
        {p.note && (
          <>
            <h3>Interne Notiz</h3>
            <p className="message">{p.note}</p>
          </>
        )}
        {(write || can('products.delete')) && (
          <div className="danger-zone">
            {write && (
              <button type="button" className="btn btn--secondary btn--sm" disabled={toggle.isPending} onClick={() => toggle.mutate(!p.isActive)}>
                {p.isActive ? 'Archivieren' : 'Wieder aktivieren'}
              </button>
            )}
            {can('products.delete') && (
              <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} onClick={() => window.confirm(`Produkt „${p.name}“ wirklich löschen? Die Bilder werden mit gelöscht.`) && remove.mutate()}>
                Löschen
              </button>
            )}
          </div>
        )}
      </section>
    </>
  );
}

/** Bilder: großes Hauptbild, darunter alle Bilder mit Pfeilen zum Sortieren, „Als Hauptbild“ und Löschen. Mehrere Dateien auf einmal hochladbar. */
function ProductImages({ product, onChange, canWrite }: { product: Product; onChange: (p: Product) => void; canWrite: boolean }) {
  const [selected, setSelected] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const images = product.images;
  const current = images[Math.min(selected, images.length - 1)];
  const free = PRODUCT_IMAGE_LIMITS.maxCount - images.length;

  const order = useMutation({
    mutationFn: async (ids: string[]) => (await api<{ data: Product }>('POST', `/products/${product.id}/images/order`, { ids })).data,
    onSuccess: onChange,
  });
  const remove = useMutation({
    mutationFn: async (imageId: string) => (await api<{ data: Product }>('DELETE', `/products/${product.id}/images/${imageId}`)).data,
    onSuccess: (p) => {
      onChange(p);
      setSelected(0);
    },
  });

  async function upload(files: File[]) {
    setErrors([]);
    setBusy(true);
    const problems: string[] = [];
    let latest: Product | null = null;
    for (const file of files.slice(0, Math.max(free, 0))) {
      if (file.size > PRODUCT_IMAGE_LIMITS.maxBytes) {
        problems.push(`${file.name}: größer als 2 MB.`);
        continue;
      }
      try {
        latest = (await apiUpload<{ data: Product }>(`/products/${product.id}/images`, file)).data;
      } catch (e) {
        problems.push(`${file.name}: ${e instanceof ApiError ? e.message : 'Hochladen fehlgeschlagen.'}`);
      }
    }
    if (files.length > free) problems.push(`Nur ${free} von ${files.length} Bildern übernommen, ein Produkt hat höchstens ${PRODUCT_IMAGE_LIMITS.maxCount} Bilder.`);
    if (latest) onChange(latest);
    setErrors(problems);
    setBusy(false);
  }
  const move = (index: number, to: number) => {
    const ids = images.map((i) => i.id);
    const [item] = ids.splice(index, 1);
    ids.splice(to, 0, item!);
    order.mutate(ids);
    setSelected(to);
  };

  return (
    <section className="card product-images" aria-label="Bilder">
      <h2>Bilder</h2>
      {current ? (
        <figure className="product-images__main">
          <img src={imageUrl(product.id, current.id)} alt={`${product.name}, Bild ${selected + 1}`} />
          <figcaption className="muted small">
            {selected === 0 ? 'Hauptbild · ' : ''}{current.fileName} · {current.width} × {current.height} px · {sizeText(current.sizeBytes)}
          </figcaption>
        </figure>
      ) : (
        <div className="product-images__empty muted">Noch keine Bilder.</div>
      )}
      {images.length > 0 && (
        <ul className="product-images__list">
          {images.map((img, i) => (
            <li key={img.id} className={i === selected ? 'is-selected' : undefined}>
              <button type="button" className="product-images__thumb" onClick={() => setSelected(i)} aria-label={`Bild ${i + 1} ansehen`} aria-pressed={i === selected}>
                <img src={imageUrl(product.id, img.id)} alt="" loading="lazy" />
                {i === 0 && <span className="product-images__badge">Hauptbild</span>}
              </button>
              {canWrite && (
                <div className="product-images__tools">
                  <button type="button" className="btn btn--secondary btn--sm" disabled={i === 0 || order.isPending} onClick={() => move(i, i - 1)} aria-label={`Bild ${i + 1} nach vorn`}>←</button>
                  <button type="button" className="btn btn--secondary btn--sm" disabled={i === images.length - 1 || order.isPending} onClick={() => move(i, i + 1)} aria-label={`Bild ${i + 1} nach hinten`}>→</button>
                  {i > 0 && <button type="button" className="btn btn--secondary btn--sm" disabled={order.isPending} onClick={() => move(i, 0)} aria-label={`Bild ${i + 1} als Hauptbild`} title="Als Hauptbild">★</button>}
                  <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} onClick={() => window.confirm(`Bild „${img.fileName}“ löschen?`) && remove.mutate(img.id)} aria-label={`Bild ${i + 1} löschen`}>×</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {canWrite && (
        <div className="product-images__upload">
          <label className={`btn btn--secondary${free <= 0 || busy ? ' is-disabled' : ''}`}>
            {busy ? 'Wird hochgeladen …' : 'Bilder hochladen'}
            <input
              type="file"
              accept="image/webp,image/png,image/jpeg"
              multiple
              className="sr-only"
              disabled={free <= 0 || busy}
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                e.target.value = '';
                if (files.length) void upload(files);
              }}
            />
          </label>
          <small className="muted">WebP empfohlen, auch PNG oder JPEG. Höchstens 2 MB je Bild und {PRODUCT_IMAGE_LIMITS.maxCount} Bilder, noch {Math.max(free, 0)} frei. Das erste Bild ist das Hauptbild.</small>
        </div>
      )}
      {errors.length > 0 && (
        <div className="alert alert--error" role="alert">
          {errors.map((e) => <div key={e}>{e}</div>)}
        </div>
      )}
      {(order.error || remove.error) && <div className="alert alert--error" role="alert">{(order.error ?? remove.error)!.message}</div>}
    </section>
  );
}
